// src/services/dashboardService.js
import api from "./api";

// Extrai contagens de respostas que podem vir como:
// - number
// - "123"
// - { total: 123 } / { count: 123 } / { value: 123 }
// - DTOs específicos: { totalTexts }, { totalActivities }, { totalNotes }
function extractCount(response, debugLabel = "unknown") {
  const data = response?.data;

  if (typeof data === "number" && !Number.isNaN(data)) return data;

  if (typeof data === "string") {
    const n = parseInt(data, 10);
    if (!Number.isNaN(n)) return n;
  }

  if (data && typeof data === "object") {
    // candidatos “genéricos”
    const candidates = ["total", "count", "value"];

    // candidatos “do seu domínio”
    // (seu controller retorna DTOs, então isso ajuda)
    const domainCandidates = [
      "totalTexts",
      "totalActivities",
      "totalNotes",
      "textsTotal",
      "activitiesTotal",
      "notesTotal",
    ];

    for (const key of [...domainCandidates, ...candidates]) {
      if (data[key] != null) {
        const v = data[key];
        if (typeof v === "number" && !Number.isNaN(v)) return v;
        if (typeof v === "string") {
          const n = parseInt(v, 10);
          if (!Number.isNaN(n)) return n;
        }
      }
    }

    // fallback: se o DTO tiver 1 único campo numérico, pega ele
    const numericKeys = Object.keys(data).filter((k) => {
      const v = data[k];
      return (
        typeof v === "number" ||
        (typeof v === "string" && v.trim() !== "" && !Number.isNaN(+v))
      );
    });

    if (numericKeys.length === 1) {
      const v = data[numericKeys[0]];
      return typeof v === "number" ? v : parseInt(v, 10) || 0;
    }
  }

  console.warn(
    `[dashboardService] Não consegui extrair número de ${debugLabel}:`,
    data,
  );
  return 0;
}

const dashboardService = {
  // =========================
  // (1) /texts/total
  // (2) /activities/total
  // (3) /notes/total
  // =========================
  async getTotalTexts() {
    const res = await api.get("/Dashboard/texts/total");
    return extractCount(res, "Dashboard/texts/total");
  },

  async getTotalActivities() {
    const res = await api.get("/Dashboard/activities/total");
    return extractCount(res, "Dashboard/activities/total");
  },

  async getTotalNotes() {
    const res = await api.get("/Dashboard/notes/total");
    return extractCount(res, "Dashboard/notes/total");
  },

  // Conveniência (mantém seu padrão atual)
  async getUserStats() {
    const [textsRes, activitiesRes, notesRes] = await Promise.all([
      api.get("/Dashboard/texts/total"),
      api.get("/Dashboard/activities/total"),
      api.get("/Dashboard/notes/total"),
    ]);

    return {
      totalTexts: extractCount(textsRes, "Dashboard/texts/total"),
      totalActivities: extractCount(
        activitiesRes,
        "Dashboard/activities/total",
      ),
      totalNotes: extractCount(notesRes, "Dashboard/notes/total"),
    };
  },

  // =========================
  // (4) /last-annotated-texts?take=5
  // =========================
  async getLastAnnotatedTexts(take = 5) {
    const res = await api.get("/Dashboard/last-annotated-texts", {
      params: { take },
    });
    return res.data;
  },

  // =========================
  // (5) /followed-users-last-actions?take=5
  // =========================
  async getFollowedUsersLastActions(take = 5) {
    const res = await api.get("/Dashboard/followed-users-last-actions", {
      params: { take },
    });
    return res.data;
  },

  // =========================
  // (6) /active-users-last-actions?take=5
  // =========================
  async getActiveUsersLastActions(take = 5) {
    const res = await api.get("/Dashboard/active-users-last-actions", {
      params: { take },
    });
    return res.data;
  },

  // =========================
  // (7) /notes-ranking?take=10
  // =========================
  async getNotesRanking(take = 10) {
    const res = await api.get("/Dashboard/notes-ranking", {
      params: { take },
    });
    return res.data;
  },

  // =========================
  // (8) /notes-chart?range=month
  // =========================
  async getNotesChart(range = "month") {
    const res = await api.get("/Dashboard/notes-chart", {
      params: { range },
    });
    return res.data;
  },

  // ==================================
  // (fora do DashboardController) FOLLOW
  // (mantive porque você já usa no front)
  // ==================================
  async getFollowersCount(userId) {
    const res = await api.get(`/Follow/${userId}/followers/count`);
    return extractCount(res, "Follow/{userId}/followers/count");
  },

  async getFollowingCount(userId) {
    const res = await api.get(`/Follow/${userId}/following/count`);
    return extractCount(res, "Follow/{userId}/following/count");
  },

  async getFollowers(userId) {
    const res = await api.get(`/Follow/followers/${userId}`);
    return res.data;
  },

  async getFollowing(userId) {
    const res = await api.get(`/Follow/following/${userId}`);
    return res.data;
  },
};

export default dashboardService;
